import { createHttpService } from '../ApiService';
import { normalizePaginatedResponse } from '../__shared/paginationUtils';
import { TDocument } from '../documents/documents.interfaces';
import {
  TProfileCalendarApiResponse,
  TProfileDocumentsApiResponse,
} from './profile.interfaces';
import { mockEventsList } from '../../__mocks__/events/eventsList';
import { TEventsPrimary } from '../events/events.interfaces';
import { IProfilePersonalDetailsApiResponse } from './profilePersonalDetails.interfaces';
import { mockProfileDetails } from 'src/__mocks__/profile/profileDetails.mock';
import { mockProfileDocuments } from 'src/__mocks__/profile/profileDocuments.mock';
import { TPaginationParams } from '../__shared/shared.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

export const useProfileService = () => {
  const httpAPI = createHttpService();
  const withMockData = isAppWithMock();

  /**
   * Get Profile Personal Details
   * GET /profile/:userId
   */
  const getProfileDetails = (
    userId: string
  ): Promise<IProfilePersonalDetailsApiResponse> => {
    if (withMockData) {
      return Promise.resolve(mockProfileDetails);
    }
    return httpAPI.profile.get<IProfilePersonalDetailsApiResponse>(
      `/${userId}`
    );
  };

  /**
   * Get Profile Documents with Pagination
   * GET /profile/:userId/documents?page=1&limit=10
   */
  const getProfileDocuments = (
    userId: string,
    _params: TPaginationParams
  ): Promise<TProfileDocumentsApiResponse> => {
    if (withMockData) {
      return Promise.resolve(
        normalizePaginatedResponse(
          {
            data: mockProfileDocuments,
            count: mockProfileDocuments.length,
            page: 1,
            limit: 10,
          },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
      },
    };

    return httpAPI.profile
      .get<any>(`/${userId}/documents`, requestObj)
      .then((res) => {
        const body = res?.data ?? res;
        if (body && (Array.isArray(body.data) || body.data === undefined)) {
          return normalizePaginatedResponse<TDocument>(body, 'data');
        }
        return normalizePaginatedResponse<TDocument>({}, 'data');
      })
      .catch(() =>
        normalizePaginatedResponse(
          {
            data: mockProfileDocuments.slice(
              (Number(_params.page) - 1) * Number(_params.limit),
              Number(_params.page) * Number(_params.limit)
            ),
            count: mockProfileDocuments.length,
            page: Number(_params.page) || 1,
            limit: Number(_params.limit) || 10,
          },
          'data'
        )
      );
  };

  /**
   * Get Profile Calendar List
   * GET /profile/calendar
   */
  const getProfileCalendarList = (): Promise<TProfileCalendarApiResponse> => {
    if (withMockData) {
      return Promise.resolve(
        normalizePaginatedResponse(
          {
            data: mockEventsList,
            total: mockEventsList.length,
            page: 1,
            limit: 10,
          },
          'data'
        ) as TProfileCalendarApiResponse
      );
    }
    return httpAPI.profile
      .get<any>('/calendar')
      .then((res) => {
        const body = res?.data ?? res;
        return normalizePaginatedResponse(body ?? {}, 'data') as TProfileCalendarApiResponse;
      });
  };

  /**
   * Get Profile Calendar Item
   * GET /profile/calendar/:eventId
   */
  const getProfileCalendarItem = (eventId: string): Promise<TEventsPrimary> => {
    if (withMockData) {
      return Promise.resolve(
        mockEventsList.find((event) => event.id === eventId) as TEventsPrimary
      );
    }
    return httpAPI.profile.get<TEventsPrimary>(`/calendar/${eventId}`);
  };

  return {
    getProfileDetails,
    getProfileDocuments,
    getProfileCalendarList,
    getProfileCalendarItem,
  };
};
